// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { AuthCard, AuthScene, Stepper } from '../components/authui';
import { SignedInAs } from './OnboardingGate';
import { MoneyInput } from '../ui/MoneyInput';

// The step Sam never got: right after the domain verifies, ask the one question the
// whole product depends on — what do you charge per hour? — and create the first
// technician (the signing-up human) with that rate. Without it, day one ends in $0
// time entries; with it, the first logged minute is worth real money. Skippable, and
// never shown again once a technician exists.

function nameFromEmail(email: string | undefined): string {
  const local = (email ?? '').split('@')[0] ?? '';
  if (!local) return '';
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function SetupShopScreen({ onDone, onSkip }: { onDone: () => void; onSkip: () => void }) {
  const { api, session } = useAuth();

  const [name, setName] = useState(() => nameFromEmail(session?.user?.email ?? undefined));
  const [billMinor, setBillMinor] = useState<number | null>(null);
  const [costMinor, setCostMinor] = useState<number | null>(null);
  const [clientName, setClientName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const finish = useMutation({
    mutationFn: async () => {
      await api.createTechnician({
        name: name.trim(),
        ...(session?.user?.email ? { email: session.user.email } : {}),
        default_bill_rate_minor: billMinor!,
        ...(costMinor != null ? { cost_rate_minor: costMinor } : {}),
      });
      // The founder IS this technician: link the login (by email) so "my tickets", "my tasks"
      // and the workload views know who they are. Best effort — the shop is set up either way.
      try {
        await api.linkMeToTechnician();
      } catch {
        /* the Home banner offers "Link me to a technician" as the fallback */
      }
      if (clientName.trim()) {
        await api.createClient({ name: clientName.trim() });
      }
    },
    onSuccess: onDone,
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const canFinish = name.trim().length > 0 && billMinor != null && billMinor > 0 && !finish.isPending;

  return (
    <AuthScene>
      <AuthCard footer={<SignedInAs />}>
        <Stepper current={3} />
        <div className="auth-head">
          <h1>Set up your shop</h1>
          <p>One question matters before your first invoice: what’s an hour of your work worth?</p>
        </div>
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (canFinish) finish.mutate();
          }}
        >
          <div className="field">
            <label htmlFor="setup-name">Your name</label>
            <input
              id="setup-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="How you’ll appear on timesheets"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="setup-bill">What do you charge per hour?</label>
            <MoneyInput id="setup-bill" valueMinor={billMinor} onChange={setBillMinor} placeholder="150.00" autoFocus />
            <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
              Every hour you log is priced at this rate unless a client has its own. You can change it any time.
            </span>
          </div>
          <div className="field">
            <label htmlFor="setup-cost">
              What does an hour cost you? <span className="muted">(optional — powers profit numbers)</span>
            </label>
            <MoneyInput id="setup-cost" valueMinor={costMinor} onChange={setCostMinor} placeholder="0.00" />
          </div>
          <div className="field">
            <label htmlFor="setup-client">
              Your first client <span className="muted">(optional)</span>
            </label>
            <input
              id="setup-client"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Acme Corp"
            />
          </div>

          {error && <div className="auth-msg err">⚠ {error}</div>}

          <button type="submit" className="btn primary block lift" disabled={!canFinish}>
            {finish.isPending ? 'Setting up…' : 'Finish setup'}
          </button>
          <p className="auth-alt" style={{ marginTop: 'var(--sp-2)' }}>
            <button type="button" className="linklike sm" onClick={onSkip} disabled={finish.isPending}>
              Skip for now — I’ll set rates later
            </button>
          </p>
        </form>
      </AuthCard>
    </AuthScene>
  );
}
